// Curator fields are fragments ("recessive Stargardt disease"), sometimes
// wrapped in <p>, carrying inline markup, split into lines with <br> by the
// portal editor, and occasionally ending in a cross-link or a stray ";".
// The per-gene page presents each line as a sentence.
//
// The stored text is never changed; this is presentation on the way to the
// DOM. Per line or paragraph: the capital is applied only when the first
// word is plain lowercase letters, so case-significant tokens (cGMP, cAMP,
// miR-204, c.2588G>C, rd10) keep their case; the period goes after the last
// visible text and any inline closers, and replaces a trailing ; , or :.
// Empty trailing lines (and a trailing <br>) are dropped; input with no visible text yields null
// so callers can drop the field. Mirror: custom.css `.gene-items` has no
// ::first-letter rule on purpose; the capital is decided here.
//
// JS `\s` already matches U+00A0, so plain \s covers a literal no-break space.

const BLOCK_CLOSER = /^<\/(p|div|li|ul|ol|blockquote)>$/i;
const NBSP_ENTITY = /&nbsp;/gi;
// Boundaries between lines: a <br>, or the gap between two paragraphs.
const LINE_BREAK = /(<br\s*\/?>|<\/p>\s*<p[^>]*>)/i;
// A plain word: lowercase letters, hyphen or apostrophe, followed by space,
// end, or ordinary punctuation. A "." is deliberately not in the set, which
// leaves c.2588G>C and p.Gly1961Glu alone.
const PLAIN_WORD = /^[a-z][a-z'’-]*(?=[\s,;:/()"&]|$)/;

// Splits `html` into [body, tail] where tail is the run of trailing block
// closers and whitespace. Backwards scan, so it is linear. A trailing <br>
// is left in the body: it is a line break before an empty last line, which
// asSentence drops.
function splitTail(html) {
  let i = html.length;
  for (;;) {
    let j = i;
    while (j > 0 && /\s/.test(html[j - 1])) j--;
    if (j === 0 || html[j - 1] !== '>') break;
    const lt = html.lastIndexOf('<', j - 1);
    if (lt < 0) break;
    const tag = html.slice(lt, j);
    if (!BLOCK_CLOSER.test(tag)) break;
    i = lt;
  }
  return [html.slice(0, i), html.slice(i)];
}

// Trailing whitespace and &nbsp; removed with a backwards loop (a `\s+$`
// regex is quadratic on a long run of blanks that is not at the end).
function trimEndBlank(s) {
  let i = s.length;
  for (;;) {
    if (i > 0 && /\s/.test(s[i - 1])) { i--; continue; }
    if (i >= 6 && s.slice(i - 6, i).toLowerCase() === '&nbsp;') { i -= 6; continue; }
    return s.slice(0, i);
  }
}

export function visibleText(html) {
  return trimEndBlank(html.replace(/<[^>]*>/g, '').replace(NBSP_ENTITY, ' '));
}

function isTerminated(text) {
  return /[.!?…]$/.test(text) || /&hellip;$/i.test(text);
}

function capitalise(body) {
  const m = body.match(/^((?:\s*<[^>]*>)*\s*)([^<]*)/);
  const lead = m[1], text = m[2];
  if (!PLAIN_WORD.test(text)) return body;
  return lead + text[0].toUpperCase() + body.slice(lead.length + 1);
}

// The last ; , or : before the end, allowing inline closers after it, and
// not the ";" of an entity such as &nbsp; or &alpha;.
function trailingPunctuation(body) {
  const m = body.match(/[;,:](?=(?:\s*<\/[a-z]+>)*\s*$)/i);
  if (!m) return null;
  if (m[0] === ';' && /&[a-z0-9#]+$/i.test(body.slice(0, m.index))) return null;
  return m.index;
}

// One line or paragraph, or null when it has no visible text.
function sentence(segment) {
  let body = trimEndBlank(segment);
  if (!visibleText(body).trim()) return null;
  body = capitalise(body);
  if (isTerminated(visibleText(body))) return body;
  const punct = trailingPunctuation(body);
  if (punct != null) {
    body = trimEndBlank(body.slice(0, punct)) + body.slice(punct + 1);
    if (isTerminated(visibleText(body))) return body;
  }
  // After the last visible text and any inline closers (<a>, <i>) that
  // end the line, so the period is never link or italic text.
  return trimEndBlank(body) + '.';
}

export function asSentence(html) {
  if (!html) return null;
  const [body, tail] = splitTail(html);
  const parts = body.split(LINE_BREAK);           // segment, separator, segment, ...
  const out = [];
  for (let i = 0; i < parts.length; i += 2) {
    const s = sentence(parts[i]);
    out.push(s === null ? '' : s);
    if (i + 1 < parts.length) out.push(parts[i + 1]);
  }
  // Drop empty trailing lines together with the separators before them.
  while (out.length && out[out.length - 1] === '') { out.pop(); out.pop(); }
  if (!out.length) return null;
  return out.join('') + tail;
}
